import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DATESTR_FMT } from 'tdr-common';
import { HASH_ELEMENT_IDS } from '../../common/hashElementIds';
import DateUnchecked from '../../icons/date-unchecked.svg';
import GuestUnchecked from '../../icons/guest-unchecked.svg';
import TimeUnchecked from '../../icons/time-unchecked.svg';
import { SearchFilters } from '../../routes/AvailabilityGuard';
import styles from './BookingDetailsSummary.module.scss';
import { useTranslation } from 'react-i18next';

type BookingDetailsSummaryProps = {
	searchFilters: SearchFilters;
};

export const BookingDetailsSummary = ({ searchFilters }: BookingDetailsSummaryProps) => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const handleClick = (hash: string) => {
		navigate({
			pathname: location.pathname,
			hash
		});
	};

	const isFiltersEmpty = !Object.values(searchFilters).some((filter) => filter !== undefined && filter !== null); // true
	const groupSizeIconURL: string = GuestUnchecked;
	const dateIconURL: string = DateUnchecked;
	const timeIconURL: string = TimeUnchecked;

	return (
		<div className={styles.FiltersHeader}>
			<FilterIcon
				key={`${searchFilters.groupSize}`}
				icon={<img src={groupSizeIconURL} alt={`${t('labels.group_size')} ${searchFilters.groupSize ? t('labels.set_icon') : t('labels.unset_icon')} Icon`} />}
				text={`${searchFilters.groupSize}`}
				onClick={() => handleClick(HASH_ELEMENT_IDS.groupSize)}
			/>
			{isFiltersEmpty || !!searchFilters.date ? (
				<FilterIcon
					key={`${searchFilters.date}-${i18n.language}`}
					icon={<img src={dateIconURL} alt={`${t('labels.date')} ${searchFilters.date ? t('labels.set_icon') : t('labels.unset_icon')} Icon`} />}
					text={formatDate(searchFilters.date, i18n.language)}
					onClick={() => handleClick('calendar')}
					grow
				/>
			) : null}
			{isFiltersEmpty || !!searchFilters.time ? (
				<FilterIcon
					key={`${searchFilters.time}`}
					icon={<img src={timeIconURL} alt={`${t('labels.time')} ${searchFilters.time ? t('labels.set_icon') : t('labels.unset_icon')} Icon`} />}
					text={formatTime(searchFilters.time)}
					grow
					onClick={() => handleClick(HASH_ELEMENT_IDS.timeslots)}
				/>
			) : null}
			{!isFiltersEmpty && (!searchFilters?.time || !searchFilters?.date) ? (
				<>
					<FilterIcon
						key={'params_missing_params'}
						text={!searchFilters?.date ? t('actions.select_date') : t('actions.set_time')}
						icon={
							<img
								src={!!searchFilters?.date ? timeIconURL : dateIconURL}
								alt={`${!!searchFilters.date ? t('labels.time') : t('labels.date')} ${t('labels.unset_icon')} Icon`}
							/>
						}
						variant='filled'
						styleOverride={{ textTransform: 'capitalize', padding: '10px 7px' }}
						grow
						onClick={() => handleClick(!searchFilters?.date ? HASH_ELEMENT_IDS.calendar : HASH_ELEMENT_IDS.timeslots)}
					/>
				</>
			) : null}
		</div>
	);
};

const formatDate = (dateFilter?: string, language?: string) => {
	if (!dateFilter) {
		return '';
	}
	const locale = language === 'fr' ? 'fr-CA' : 'en';
	return DateTime.fromFormat(dateFilter, DATESTR_FMT)
		.setLocale(locale)
		.toLocaleString({ month: 'long', day: 'numeric' });
};

const formatDateShort = (dateFilter?: string, language?: string) => {
	if (!dateFilter) {
		return '';
	}
	const locale = language === 'fr' ? 'fr-CA' : 'en';
	return DateTime.fromFormat(dateFilter, DATESTR_FMT)
		.setLocale(locale)
		.toLocaleString({ month: 'short', day: 'numeric' });
};

const formatTime = (timeFilter?: string) => {
	return timeFilter ? DateTime.fromFormat(timeFilter, 'HH:mm').toFormat('h:mma') : '';
};

type FilterIconProps = {
	icon?: React.ReactNode;
	variant?: 'filled' | 'outlined' | 'text';
	grow?: boolean;
	text?: string;
	ariaLabel?: string;
	onClick?: () => void;
	styleOverride?: React.CSSProperties;
	date?: string
};
const FilterIcon = ({ icon, text, ariaLabel, onClick, styleOverride, grow, date = '' }: FilterIconProps) => {
	const { i18n } = useTranslation();
	const buttonTextRef = useRef<HTMLElement>(null);
	const [displayText, setDisplayText] = useState<string>(text);

	useEffect(() => {
		const checkOverflow = () => {
			const isOverflowing = buttonTextRef?.current?.clientWidth !== buttonTextRef?.current?.scrollWidth;
			if (!!date && isOverflowing) {
				setDisplayText(formatDateShort(date, i18n.language));
			}
		};
		checkOverflow();
		window.addEventListener('resize', checkOverflow);
		return () => window.removeEventListener('resize', checkOverflow);
	}, [date, text, buttonTextRef.current, i18n.language]);
	return (
		<button
			onClick={onClick}
			className={`bg-tablz-med-gray-2 rounded-2xl border border-white/10 flex gap-1 p-5 items-center justify-center overflow-hidden ${grow && 'grow'} hover:bg-tablz-med-gray-6`}
			style={{ ...styleOverride }}
			aria-label={ariaLabel}
		>
			{icon}
			<span ref={buttonTextRef} className="font-poppins text-zinc-50 font-semibold overflow-hidden whitespace-nowrap capitalize">{displayText}</span>
		</button>
	);
};
